import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faMapMarkerAlt, faCalendarAlt, faSearch, faTimes, faMap } from '@fortawesome/free-solid-svg-icons';
import { TranslateModule } from '@ngx-translate/core';
import { Event } from '../../interfaces/event';
import { SkeletonComponent } from '../skeleton/skeleton.component';
import { ButtonComponent } from '../button/button.component';
import { MapComponent } from '../map/map.component';
import { EventCardComponent } from '../event-card/event-card.component';

interface LocationFilter {
  address: string;
  branchId: number;
  breweryName: string;
}

@Component({
  selector: 'app-events-showcase',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    FontAwesomeModule,
    TranslateModule,
    SkeletonComponent,
    ButtonComponent,
    MapComponent,
    EventCardComponent,
  ],
  templateUrl: './events-showcase.component.html',
  styleUrls: ['./events-showcase.component.scss'],
})
export class EventsShowcaseComponent implements OnInit, OnChanges {
  @Input() events: Event[] = [];
  @Input() isLoading: boolean = false;
  @Input() showFilters: boolean = true;
  @Input() showTitle: boolean = true;
  @Input() title: string = '';
  @Input() maxEvents: number = 0;
  @Input() compact: boolean = false;

  // Icons
  faMapMarkerAlt = faMapMarkerAlt;
  faCalendarAlt = faCalendarAlt;
  faSearch = faSearch;
  faTimes = faTimes;
  faMap = faMap;

  // State
  filteredEvents: Event[] = [];
  selectedEventId: number | null = null;
  locationFilters: LocationFilter[] = [];
  selectedLocationFilter: string = '';
  searchQuery: string = '';

  // Map popup
  activeMapEventId: number | null = null;

  constructor(private router: Router) {}

  // ── Lifecycle ────────────────────────────────────────

  ngOnInit(): void {
    if (this.events.length > 0) {
      this.extractLocationFilters();
    }
    this.applyFilters();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['events'] || changes['isLoading']) {
      if (this.events.length > 0) {
        this.extractLocationFilters();
      }
      this.applyFilters();
    }
  }

  // ── Map Popup ────────────────────────────────────────

  openMapPopup(eventId: number): void {
    if (this.activeMapEventId === eventId) {
      this.closeMapPopupInstant();
      return;
    }
    this.activeMapEventId = eventId;
  }

  closeMapPopupInstant(): void {
    this.activeMapEventId = null;
  }

  getMapEventBranches(event: Event) {
    if (event.locationType === 2 && event.latitude && event.longitude) {
      // For custom location events, create a synthetic eventBranch-like structure
      return [];
    }
    return event.eventBranches || [];
  }

  getMapCustomCoords(event: Event): [number, number] | undefined {
    if (event.locationType === 2 && event.latitude && event.longitude) {
      return [event.latitude, event.longitude];
    }
    return undefined;
  }

  // ── Filters ──────────────────────────────────────────

  private normalizeStr(str: string): string {
    return str.toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  private extractLocationFilters(): void {
    const seen = new Map<string, string>(); // normalized -> original display value

    this.events.forEach(event => {
      if (event.eventBranches) {
        event.eventBranches.forEach(eb => {
          if (eb.branch && eb.branch.address) {
            const key = this.normalizeStr(eb.branch.address);
            if (!seen.has(key)) {
              seen.set(key, eb.branch.address.trim());
            }
          }
        });
      }
      if (event.locationType === 2 && event.address) {
        const key = this.normalizeStr(event.address);
        if (!seen.has(key)) {
          seen.set(key, event.address.trim());
        }
      }
    });

    this.locationFilters = Array.from(seen.values())
      .sort((a, b) => a.localeCompare(b))
      .map(address => ({ address, branchId: 0, breweryName: '' }));
  }

  applyFilters(): void {
    let result = [...this.events];

    if (this.selectedLocationFilter) {
      const filterKey = this.normalizeStr(this.selectedLocationFilter);
      result = result.filter(event => {
        if (event.eventBranches) {
          const hasMatch = event.eventBranches.some(
            eb => eb.branch?.address && this.normalizeStr(eb.branch.address) === filterKey
          );
          if (hasMatch) return true;
        }
        if (event.locationType === 2 && event.address && this.normalizeStr(event.address) === filterKey) {
          return true;
        }
        return false;
      });
    }

    if (this.searchQuery.trim()) {
      const query = this.searchQuery.toLowerCase().trim();
      result = result.filter(
        event =>
          event.title.toLowerCase().includes(query) ||
          event.description?.toLowerCase().includes(query) ||
          event.eventType?.name?.toLowerCase().includes(query)
      );
    }

    this.filteredEvents = result;
  }

  onLocationFilterChange(address: string): void {
    this.selectedLocationFilter = address;
    this.applyFilters();
  }

  onSearchChange(): void {
    this.applyFilters();
  }

  clearFilters(): void {
    this.selectedLocationFilter = '';
    this.searchQuery = '';
    this.applyFilters();
  }

  get hasActiveFilters(): boolean {
    return !!this.selectedLocationFilter || !!this.searchQuery.trim();
  }

  // ── Helpers ──────────────────────────────────────────

  getDisplayEvents(): Event[] {
    const events = this.filteredEvents;
    if (this.maxEvents > 0) {
      return events.slice(0, this.maxEvents);
    }
    return events;
  }

  getEventLocation(event: Event): string {
    if (event.locationType === 2 && event.address) {
      return event.address;
    }
    if (event.eventBranches && event.eventBranches.length > 0) {
      const first = event.eventBranches[0]?.branch;
      if (first) {
        const count = event.eventBranches.length;
        return count > 1 ? `${count} sucursales` : first.address;
      }
    }
    return '';
  }

  getFinalPrice(event: Event): number {
    return event.price - (event.discountAmount || 0);
  }

  hasDiscount(event: Event): boolean {
    return event.discountAmount > 0;
  }

  goToEvent(event: Event): void {
    this.router.navigate(['/event', event.slug]);
  }

  goToNewHop(): void {
    this.router.navigate(['/new-hop']);
  }
}
